/**
 * "Solicitar ao paciente": quando falta pedido médico ou carteirinha em dia, abre a
 * mensagem pronta para copiar ou abrir no WhatsApp já endereçado à pessoa.
 * Mesmo componente no detalhe do agendamento e no card "Convênios a conferir".
 * Não renderiza nada quando os avisos não pedem nada à pessoa.
 * `compact`: só o ícone, com dica (linha do card do Início).
 */
import React, { useState } from 'react';
import { Box, TextField, Tooltip, Typography } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PopupBase from '../../../../../popup/base/PopupBase';
import CustomButton from '../../../../../components/buttons/CustomButton';
import ColorPalette from '../../../../../colorpalette/ColorPalette';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { SendWhatsAppMessage } from '../../../../../api/WhatsappAPI';
import { copyToClipboard } from '../../utils/copyUtils';
import { askableItems, buildInsuranceRequestMessage } from '../../utils/insuranceRequestMessage';
import styles from './InsuranceAlertList.module.css';

function AskPersonButton({ alerts, patientId, date, providerName, compact = false }) {
  const { CompanyConfig, GetPatientById } = DataProviderInstance();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);

  const items = askableItems(alerts);
  if (items.length === 0) return null;

  const person = patientId != null ? GetPatientById(patientId) : null;
  const phone = person?.phone || '';

  const openPopup = () => {
    setCopied(false);
    setMessage(buildInsuranceRequestMessage({
      companyName: CompanyConfig?.Name || '',
      personName: person?.name || '',
      date,
      providerName,
      items,
    }));
    setOpen(true);
  };

  const handleCopy = async () => {
    const result = await copyToClipboard(message);
    if (result?.success) setCopied(true);
  };

  const handleWhatsApp = () => {
    SendWhatsAppMessage(phone, encodeURIComponent(message), person?.countryCode || '');
  };

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0' }}>
      <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
        Mensagem pronta com o que falta para o atendimento pelo convênio. Ajuste o texto se quiser.
      </Typography>
      <TextField
        value={message}
        onChange={(e) => { setMessage(e.target.value); setCopied(false); }}
        multiline
        minRows={7}
        fullWidth
        inputProps={{ 'aria-label': 'Mensagem' }}
        sx={{ '& textarea': { minHeight: '220px' } }}
      />
      {!phone && (
        <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
          Sem telefone no cadastro: copie a mensagem e envie pela conversa.
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <CustomButton
          variant="outlined"
          style="secondary"
          label={copied ? 'Mensagem copiada' : 'Copiar mensagem'}
          icon={<ContentCopyOutlinedIcon />}
          onClick={handleCopy}
        />
        <CustomButton
          variant="contained"
          style="secondary"
          label="Abrir no WhatsApp"
          icon={<WhatsAppIcon />}
          enabled={Boolean(phone)}
          onClick={handleWhatsApp}
        />
      </Box>
    </Box>
  );

  // O card do Início abre o atendimento no clique da linha: nada daqui pode subir até lá
  return (
    <span className={styles.askWrap} onClick={(e) => e.stopPropagation()}>
      {compact ? (
        <Tooltip title="Solicitar ao paciente" arrow>
          <button type="button" className={styles.askIconButton} onClick={openPopup} aria-label="Solicitar ao paciente">
            <WhatsAppIcon className={styles.icon} />
          </button>
        </Tooltip>
      ) : (
        <button type="button" className={styles.askButton} onClick={openPopup}>
          <WhatsAppIcon className={styles.icon} />
          Solicitar ao paciente
        </button>
      )}
      {open && (
        <PopupBase
          Title="Solicitar documento ao paciente"
          Content={content}
          OnClose={() => setOpen(false)}
          maxWidth="sm"
        />
      )}
    </span>
  );
}

export default AskPersonButton;
